import { useCallback, useEffect, useRef, useState } from "react";
import commonservice from "@apis/commonservice";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import { useTiktokAuth } from "@hooks/useTiktokAuth";
import { usePopoverGoogleAuth } from "@hooks/usePopoverGoogleAuth";
import { ILoginProps } from "@models/home";
import report, { reportImmediately } from "@report";
import { getSafeRedirectURL } from "@routes";
import { updateLoginMethod, updateSubmitLoading, updateUserInfo } from "@store/globalSlice";
import { IUserinfo, LoginMethod } from "@store/index";
import { LoginMethodStorage } from "@utils/storage";
import { initDeviceId } from "@utils/util";
import { handleUserInfo } from "@layouts/PageSider/AccountDrawer/hooks";
import useConfirmModal from "../signup/useConfirmModal";
import { revertEmailPhoneMap, trackRegisterSuccess } from "../utils";
import { useCheckUsIp } from "@hooks/useCheckUsIp";
import { usePlatformRedirect } from "@hooks/usePlatformRedirect";
import { LOCALSTORAGE_KEY } from "@config";
import { getProfile } from "@apis/http/api";
import message from "@utils/message";
import { t } from "i18next";

export const useLoginMethod = () => {
  const { loginMethod } = useAppSelector((state) => state.globalReducer);
  const isEmail = loginMethod == "email";
  const isPassword = loginMethod == "password";
  const isSMS = loginMethod == "sms";
  const isEmailPassword = loginMethod === "email_password";
  const isWxcode = loginMethod === "wxcode";

  return { loginMethod, isEmail, isPassword, isSMS, isEmailPassword, isWxcode };
};

export const useLogin = ({ redirectURL }: { redirectURL?: string }) => {
  const newRedirectURL = getSafeRedirectURL(redirectURL);
  const dispatch = useAppDispatch();
  const { loginMethod } = useLoginMethod();
  const { tipModal } = useConfirmModal();
  const { addToLoginHistory } = useLoginHistory();
  const withPasswordRef = useRef<{ setPhonePassword: Function; setEmailPassword: Function }>({
    setPhonePassword: () => {},
    setEmailPassword: () => {},
  });
  const { usProtocolDom, checkISAgreeProtocol, usWxcodeProtocolDom } = useCheckUsIp({
    isLogin: true,
  });
  const { redirectToPlatform } = usePlatformRedirect();

  const loginSuccess = async (
    data: boolean = false,
    result: any = {},
    type = "submit",
    cb?: ((arg0: IUserinfo) => any) | undefined,
    _submitType?: string,
  ) => {
    let nextType = "login";
    if (!data) {
      nextType = "signup";
      // 登录用户未注册，帮用户进行注册提示
      // 注释调旧的提示，显示新的提示文案
      // message.info(data, 3);
      trackRegisterSuccess(loginMethod, type);
    }
    localStorage.setItem("loginedNotify", "false");

    if (type !== "tiktok") {
      report.event("kalo_login", {
        kalo_pos: "login_btn",
        kalo_type: "login",
        kalo_value: loginMethod + "_success",
      });
    }

    await getProfile()
      .then(async (res) => {
        let account = "";
        let prefix = "";
        if (loginMethod === "email" || loginMethod === "email_password") {
          account = result.email;
        } else if (loginMethod === "sms" || loginMethod === "password") {
          account = result.phone;
          prefix = result.prefix;
        }
        addToLoginHistory({
          uId: res.uid,
          username: handleUserInfo(res),
          avatarUrl: res?.avatarUrl,
          havePremium: res.havePremium,
          haveStandard: res.haveStandard,
          loginTime: Date.now(),
          firstName: res?.firstName,
          lastName: res?.lastName,
          account: type === "tiktok" ? res.tiktokUserId : type === "google" ? res.googleId || account : account,
          loginMethod: type === "tiktok" ? "tiktok" : type === "google" ? "google" : loginMethod,
          prefix,
          tiktokUserHandle: res.tiktokUserHandle,
          googleName: res.googleName,
        });
        cb?.(res);
        localStorage.setItem("userName", res.username);
        localStorage.setItem("logined", "true");
        LoginMethodStorage(loginMethod);
        await reportImmediately(); // window.location 之前清空上报队列

        // 优先判断是否需要跳转到其他平台
        if (redirectToPlatform()) return;

        // 优先判断密码
        if (!res.passwordExist && type !== "tiktok" && type !== "google") {
          let search = window.location.search.replace("?", "") || "";
          search = search ? `&${search}` : "";
          window.location.href = `/add-password?type=${nextType}&phone=${result.phone || result.email}&${search}`;
          return;
        }

        dispatch(updateUserInfo(res));
        setTimeout(() => {
          dispatch(updateSubmitLoading(false));
        }, 500);

        setTimeout(() => {
          window.location.href = newRedirectURL;
        }, 500);
      })
      .catch((err) => {
        dispatch(updateSubmitLoading(false));
        message.error(err);
      });
  };

  const onSubmit = async (result: ILoginProps, submitType: string, errorCallback?: (err: any) => void) => {
    dispatch(updateSubmitLoading(true));
    await initDeviceId();
    let params: any = {
      scene: "login",
      loginMethod: loginMethod.toLocaleUpperCase(),
      tcCode: new URLSearchParams(location.search).get("tc") || localStorage.getItem("tc") || "",
    };
    if (loginMethod === "email") {
      params.email = result.email;
      params.emailCode = result.captcha;
    } else if (loginMethod === "sms") {
      params.phoneCountryCode = result.prefix;
      params.phoneNumber = result.phone;
      params.smsCode = result.captcha;
    } else if (loginMethod === "password") {
      params.phoneCountryCode = result.prefix;
      params.phoneNumber = result.phone;
      params.password = result.password;
    } else if (loginMethod === "email_password") {
      params.email = result.email;
      params.password = result.password;
    }
    commonservice
      .login(params)
      .then(async (data: any) => {
        // 登录成功后保存 token
        if (data.accessToken) {
          localStorage.setItem(LOCALSTORAGE_KEY.ACCESS_TOKEN, data.accessToken);
        }
        loginSuccess(data.exist, result, "", () => {}, submitType);
      })
      .catch(async (err) => {
        const errMsg = err?.message || err?.toString() || "";
        // 改造前的错误消息(存档记录)：
        // old err = "Already logged in. Please logout before attempting to login again."
        // old err = { "code": "AUTHENTICATION_FAILED", "message": "Wrong account or password" }
        // old err = { "code": "DATABASE_ERROR", "message": "Failed to parse stored context" }
        report.event("kalo_login", {
          kalo_pos: "login_btn",
          kalo_type: "login",
          kalo_value: loginMethod + "_error",
          kalo_json: {
            reason: {
              status: err?.status || 0,
              code: err?.code || "",
              kind: err?.kind || "",
              message: errMsg,
              rawResponse: err?.response?.data || {},
            },
          },
        });
        // SMS 或 Email 登录失败时，调用错误回调显示验证码错误
        if (err?.code === "CAPTCHA_ERROR" && errorCallback) {
          errorCallback(err);
          return;
        }
        // 密码失败报错提示
        if (loginMethod === "password") {
          withPasswordRef.current?.setPhonePassword((password: any) => ({
            ...password,
            value: result.password,
            errorMsg: errMsg,
            errorCode: err.code,
          }));
          return;
        } else if (loginMethod === "email_password") {
          withPasswordRef.current?.setEmailPassword((password: any) => ({
            ...password,
            value: result.password,
            errorMsg: errMsg,
            errorCode: err.code,
          }));
          return;
        }
        message.error(t(errMsg));
      })
      .finally(() => {
        dispatch(updateSubmitLoading(false));
      });
  };

  const { tiktokLink, SpinDom } = useTiktokAuth(
    "login",
    (data, cb) => loginSuccess(!!data?.exist, {}, "tiktok", cb, "TikTok"),
    (err?: string) => {
      console.log("授权回调失败", err);
    },
  );

  // 原有的页面跳转方式（保留作为备用）
  // const { googleLink, SpinDom: GoogleSpinDom } = useGoogleAuth(
  //   "login",
  //   (data, cb) => loginSuccess(!!data?.exist, {}, "google", cb, "Google"),
  //   (err?: string) => {
  //     console.log("Google 授权回调失败", err);
  //   },
  // );

  // 新的弹窗登录方式
  const { googlePopoverLink, SpinDom: GooglePopoverSpinDom } = usePopoverGoogleAuth(
    "login",
    (data, cb) => loginSuccess(!!data?.exist, {}, "google", cb, "Google"),
    (err?: any) => {
      console.log("Google 弹窗授权回调失败", err);
    },
  );

  const switchMethod = (type: string) => {
    if (type === "wxcode") {
      dispatch(updateLoginMethod(type));
      return;
    }
    if (type === "tiktok") {
      if (!checkISAgreeProtocol(tiktokLink)) return;
      report.click({
        kalo_pos: "click_tiktok_link",
        kalo_type: "tt_login",
      });
      tiktokLink("login");
    } else if (type === "google") {
      if (!checkISAgreeProtocol(googlePopoverLink)) return;
      report.click({
        kalo_pos: "click_google_popover_link",
        kalo_type: "google_login",
      });
      // 使用弹窗登录方式
      googlePopoverLink("login");
    } else {
      dispatch(updateLoginMethod(revertEmailPhoneMap[loginMethod]));
    }
  };

  // 统一登录，切换登录方式
  const newSwitchMethod = (type: "sms" | "email" | "password" | "email_password" | "wxcode" | "tiktok" | "google") => {
    if (type === "tiktok") {
      if (!checkISAgreeProtocol(tiktokLink)) return;
      report.click({
        kalo_pos: "click_tiktok_link",
        kalo_type: "tt_login",
      });
      tiktokLink("login");
    } else if (type === "google") {
      if (!checkISAgreeProtocol(googlePopoverLink)) return;
      report.click({
        kalo_pos: "click_google_popover_link",
        kalo_type: "google_login",
      });
      // 使用弹窗登录方式
      googlePopoverLink("login");
    } else {
      dispatch(updateLoginMethod(type));
    }
  };

  const switchWxMethod = (type: LoginMethod) => {
    console.log(type, "ppp");
    if (type === "wxcode") {
      dispatch(updateLoginMethod(type));
      return;
    }
    dispatch(updateLoginMethod(loginMethod === "wxcode" ? type : revertEmailPhoneMap[loginMethod]));
  };

  return {
    onSubmit,
    withPasswordRef,
    tipModal,
    SpinDom,
    // GoogleSpinDom,
    GooglePopoverSpinDom,
    switchMethod,
    newSwitchMethod,
    switchWxMethod,
    tiktokLink,
    // googleLink,
    googlePopoverLink,
    usProtocolDom,
    checkISAgreeProtocol,
    usWxcodeProtocolDom,
  };
};

export interface LoginHistoryItem {
  /** 推荐uId 而不是uid */
  uId: string | number;
  account: string;
  username: string;
  avatarUrl: string;
  phoneNumber?: string;
  phoneCountryCode?: string;
  havePremium: boolean;
  haveStandard: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  lastCountry?: string;
  logoutTime?: number;
  loginTime?: number;
  loginMethod: "sms" | "email" | "password" | "email_password" | "tiktok" | "wxcode" | "google";
  prefix?: string;
  level?: string;
  expired?: boolean;
  tiktokUserHandle?: string;
  googleName?: string;
}

type LegacyLoginHistoryItem = Omit<LoginHistoryItem, "uId"> & {
  uId?: string | number;
  uid?: string | number;
};

const normalizeLoginHistoryItem = (item: LegacyLoginHistoryItem): LoginHistoryItem | null => {
  const normalizedUId = item.uId ?? item.uid;
  if (normalizedUId === undefined || normalizedUId === null) return null;
  return {
    ...item,
    uId: normalizedUId,
  };
};

const normalizeLoginHistory = (items: LegacyLoginHistoryItem[]): LoginHistoryItem[] => {
  return items.map((item) => normalizeLoginHistoryItem(item)).filter((item): item is LoginHistoryItem => item !== null);
};
export const useLoginDisplay = () => {
  const [showLoginList, setShowLoginList] = useState(Boolean(localStorage.getItem("login_history_list")));
  const [loginHistory, setLoginHistory] = useState<LoginHistoryItem[]>([]);

  useEffect(() => {
    const loginHistoryString = localStorage.getItem("login_history_list");
    if (loginHistoryString) {
      try {
        const parsedHistory = JSON.parse(loginHistoryString) as LegacyLoginHistoryItem[];
        const normalizedHistory = normalizeLoginHistory(parsedHistory);
        if (normalizedHistory.length !== parsedHistory.length) {
          localStorage.setItem("login_history_list", JSON.stringify(normalizedHistory));
        }
        if (normalizedHistory.length > 0) {
          setLoginHistory(normalizedHistory.slice(0, 4));
          setShowLoginList(true);
        } else {
          setShowLoginList(false);
        }
      } catch (error) {
        console.error("解析登录历史记录失败:", error);
        setShowLoginList(false);
      }
    } else {
      setShowLoginList(false);
    }
  }, []);

  const handleSwitchToLoginArea = () => {
    setShowLoginList(false);
  };

  return { showLoginList, loginHistory, handleSwitchToLoginArea };
};

const MAX_HISTORY_ITEMS = 4;
const STORAGE_KEY = "login_history_list";

class LoginHistoryManager {
  private static instance: LoginHistoryManager;
  private history: LoginHistoryItem[];

  private constructor() {
    this.history = this.loadHistory();
  }

  public static getInstance(): LoginHistoryManager {
    if (!LoginHistoryManager.instance) {
      LoginHistoryManager.instance = new LoginHistoryManager();
    }
    return LoginHistoryManager.instance;
  }

  private loadHistory(): LoginHistoryItem[] {
    const storedHistory = localStorage.getItem(STORAGE_KEY);
    if (storedHistory) {
      try {
        const parsedHistory = JSON.parse(storedHistory) as LegacyLoginHistoryItem[];
        const normalizedHistory = normalizeLoginHistory(parsedHistory);
        if (normalizedHistory.length !== parsedHistory.length) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizedHistory));
        }
        return normalizedHistory;
      } catch (error) {
        console.error("解析登录历史记录失败:", error);
        return [];
      }
    }
    return [];
  }

  private saveHistory() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.history));
  }

  private sortHistory(history: LoginHistoryItem[]): LoginHistoryItem[] {
    return [...history].toSorted((a: LoginHistoryItem, b: LoginHistoryItem) => {
      if (a.havePremium && !b.havePremium) return -1;
      if (!a.havePremium && b.havePremium) return 1;
      if (a.haveStandard && !b.haveStandard) return -1;
      if (!a.haveStandard && b.haveStandard) return 1;
      return (b.loginTime || 0) - (a.loginTime || 0);
    });
  }

  public getHistory(): LoginHistoryItem[] {
    return this.history;
  }

  public addToHistory(userInfo: LoginHistoryItem) {
    this.history = this.history.filter((item) => item.uId !== userInfo.uId);
    this.history.unshift(userInfo);
    this.history = this.sortHistory(this.history).slice(0, MAX_HISTORY_ITEMS);
    this.saveHistory();
  }

  public updateHistory(uid: string | number, updates: Partial<LoginHistoryItem>) {
    this.history = this.history.map((item) => (item.uId === uid ? { ...item, ...updates } : item));
    this.history = this.sortHistory(this.history);
    this.saveHistory();
  }
}

export const useLoginHistory = () => {
  const [history, setHistory] = useState(() => LoginHistoryManager.getInstance().getHistory());

  useEffect(() => {
    // 每次 history 更新时，重新获取最新的历史记录
    setHistory(LoginHistoryManager.getInstance().getHistory());
  }, []);

  const addToLoginHistory = useCallback((userInfo: LoginHistoryItem) => {
    LoginHistoryManager.getInstance().addToHistory(userInfo);
    setHistory(LoginHistoryManager.getInstance().getHistory());
  }, []);

  const updateHistory = useCallback((uid: string | number, updates: Partial<LoginHistoryItem>) => {
    LoginHistoryManager.getInstance().updateHistory(uid, updates);
    setHistory(LoginHistoryManager.getInstance().getHistory());
  }, []);

  return {
    history,
    addToLoginHistory,
    updateLastCountry: (uid: string | number, lastCountry: string) => updateHistory(uid, { lastCountry }),
    updateListInfo: (uid: string | number, data: Partial<LoginHistoryItem>) => updateHistory(uid, data),
    updateAvatarUrl: (uid: string | number, avatarUrl: string) => updateHistory(uid, { avatarUrl }),
  };
};
